import { type ReactNode } from 'react'
import { Button, Tooltip } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'

/**
 * On whatever a To-dos row shows at its end at rest, its tags: hidden while the row's moves are up,
 * so the moves take their place.
 */
export const GIVES_WAY_TO_TODO_ACTIONS =
  'contents group-focus-within/cell:hidden group-hover/cell:hidden group-has-[[data-todo-actions-open]]/cell:hidden'

/**
 * The same class, but only for a row that actually has moves to give way to.
 * A resolved or lingering row renders no `TodoActions`, so hiding its tags on
 * hover would blank the row's end and put nothing in their place — take this
 * rather than the constant wherever the moves are conditional.
 */
export function givesWayToTodoActions(hasActions: boolean): string {
  return hasActions ? GIVES_WAY_TO_TODO_ACTIONS : 'contents'
}

/**
 * A To-dos row's moves, up while the row is hovered or holds focus, and while `open` says a menu
 * or picker of theirs is. Clicks and keys stop here, a portalled picker's included, so none of
 * them also opens the row.
 */
export function TodoActions({ open = false, children }: { open?: boolean; children: ReactNode }) {
  return (
    <span
      data-todo-actions-open={open ? '' : undefined}
      // 24px buttons in a 20px line, pulled 2px out top and bottom so the row keeps its height,
      // and 6px into the row's padding so they sit 6px from its edge.
      className={twMerge(
        '-my-0.5 -mr-1.5 hidden shrink-0 items-center gap-0.5 self-center',
        'group-focus-within/cell:flex group-hover/cell:flex',
        open && 'flex',
      )}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {children}
    </span>
  )
}

/** One move: a quiet 24px icon button, named by its tooltip. */
export function TodoActionButton({
  label,
  tone,
  disabled,
  children,
  onClick,
  'aria-pressed': pressed,
}: {
  label: string
  tone?: 'danger'
  disabled?: boolean
  children: ReactNode
  onClick?: () => void
  'aria-pressed'?: boolean
}) {
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <Button
            variant="ghost"
            tone={tone}
            size="sm"
            shape="square"
            aria-label={label}
            aria-pressed={pressed}
            disabled={disabled}
            onClick={onClick}
          />
        }
      >
        {children}
      </Tooltip.Trigger>
      <Tooltip.Content>{label}</Tooltip.Content>
    </Tooltip>
  )
}
